import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Input } from '../components/ui/input';
import { useLanguage } from '../contexts/LanguageContext';
import { Target, Clock, TrendingUp, Shield, User, ChevronRight, ChevronLeft, Check } from 'lucide-react';
import API from '../utils/api';

const STEPS = [
  { id: 'style', icon: TrendingUp, title: { de: 'Trading-Stil', en: 'Trading Style' } },
  { id: 'experience', icon: Clock, title: { de: 'Erfahrung', en: 'Experience' } },
  { id: 'goal', icon: Target, title: { de: 'Ziel', en: 'Goal' } },
  { id: 'time', icon: Clock, title: { de: 'Zeit', en: 'Time' } },
  { id: 'risk', icon: Shield, title: { de: 'Risiko', en: 'Risk' } },
];

export const TraderProfileOnboarding = ({ open, onOpenChange, onComplete }) => {
  const { language } = useLanguage();
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  
  const [profile, setProfile] = useState({
    trading_style: '',
    experience: '',
    goal: '',
    weekly_hours: '',
    risk_tolerance: '',
    planned_risk_per_trade: 1.0
  });

  const canProceed = () => {
    switch (step) {
      case 0: return !!profile.trading_style;
      case 1: return !!profile.experience;
      case 2: return !!profile.goal;
      case 3: return !!profile.weekly_hours;
      case 4: return !!profile.risk_tolerance;
      default: return false;
    }
  };

  const handleNext = () => {
    if (step < STEPS.length - 1) {
      setStep(step + 1);
    }
  };

  const handleBack = () => {
    if (step > 0) {
      setStep(step - 1);
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API}/trader-profile`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(profile)
      });
      
      if (response.ok) {
        onComplete?.(profile);
        onOpenChange(false);
      }
    } catch (error) {
      console.error('Error saving profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const renderStep = () => {
    switch (step) {
      case 0:
        return (
          <div className="space-y-4">
            <p className="text-muted-foreground text-sm mb-4">
              {language === 'de' 
                ? 'Dein Trading-Stil beeinflusst, wie deine Skills gewichtet werden.'
                : 'Your trading style influences how your skills are weighted.'}
            </p>
            <RadioGroup value={profile.trading_style} onValueChange={(v) => setProfile({...profile, trading_style: v})}>
              <div className="space-y-3">
                <label className={`flex items-center gap-4 p-4 rounded-lg border cursor-pointer transition-all ${profile.trading_style === 'daytrader' ? 'border-primary bg-primary/10' : 'border-zinc-800 hover:border-zinc-700'}`}>
                  <RadioGroupItem value="daytrader" id="daytrader" />
                  <div>
                    <p className="font-medium">Daytrader</p>
                    <p className="text-sm text-muted-foreground">{language === 'de' ? 'Mehrere Trades pro Tag, Positionen vor Marktschluss' : 'Multiple trades per day, close before market close'}</p>
                  </div>
                </label>
                <label className={`flex items-center gap-4 p-4 rounded-lg border cursor-pointer transition-all ${profile.trading_style === 'swing' ? 'border-primary bg-primary/10' : 'border-zinc-800 hover:border-zinc-700'}`}>
                  <RadioGroupItem value="swing" id="swing" />
                  <div>
                    <p className="font-medium">Swing Trader</p>
                    <p className="text-sm text-muted-foreground">{language === 'de' ? 'Positionen über Tage bis Wochen' : 'Positions held for days to weeks'}</p>
                  </div>
                </label>
                <label className={`flex items-center gap-4 p-4 rounded-lg border cursor-pointer transition-all ${profile.trading_style === 'position' ? 'border-primary bg-primary/10' : 'border-zinc-800 hover:border-zinc-700'}`}>
                  <RadioGroupItem value="position" id="position" />
                  <div>
                    <p className="font-medium">Position Trader</p>
                    <p className="text-sm text-muted-foreground">{language === 'de' ? 'Langfristige Positionen über Wochen bis Monate' : 'Long-term positions held for weeks to months'}</p>
                  </div>
                </label>
              </div>
            </RadioGroup>
          </div>
        );
      
      case 1:
        return (
          <div className="space-y-4">
            <p className="text-muted-foreground text-sm mb-4">
              {language === 'de' 
                ? 'Wie lange tradest du schon aktiv?'
                : 'How long have you been actively trading?'}
            </p>
            <RadioGroup value={profile.experience} onValueChange={(v) => setProfile({...profile, experience: v})}>
              <div className="space-y-3">
                <label className={`flex items-center gap-4 p-4 rounded-lg border cursor-pointer transition-all ${profile.experience === 'beginner' ? 'border-primary bg-primary/10' : 'border-zinc-800 hover:border-zinc-700'}`}>
                  <RadioGroupItem value="beginner" id="beginner" />
                  <div>
                    <p className="font-medium">{language === 'de' ? 'Anfänger' : 'Beginner'}</p>
                    <p className="text-sm text-muted-foreground">{language === 'de' ? 'Unter 1 Jahr Erfahrung' : 'Less than 1 year experience'}</p>
                  </div>
                </label>
                <label className={`flex items-center gap-4 p-4 rounded-lg border cursor-pointer transition-all ${profile.experience === 'intermediate' ? 'border-primary bg-primary/10' : 'border-zinc-800 hover:border-zinc-700'}`}>
                  <RadioGroupItem value="intermediate" id="intermediate" />
                  <div>
                    <p className="font-medium">{language === 'de' ? 'Fortgeschritten' : 'Intermediate'}</p>
                    <p className="text-sm text-muted-foreground">{language === 'de' ? '1-3 Jahre Erfahrung' : '1-3 years experience'}</p>
                  </div>
                </label>
                <label className={`flex items-center gap-4 p-4 rounded-lg border cursor-pointer transition-all ${profile.experience === 'advanced' ? 'border-primary bg-primary/10' : 'border-zinc-800 hover:border-zinc-700'}`}>
                  <RadioGroupItem value="advanced" id="advanced" />
                  <div>
                    <p className="font-medium">{language === 'de' ? 'Erfahren' : 'Advanced'}</p>
                    <p className="text-sm text-muted-foreground">{language === 'de' ? 'Über 3 Jahre Erfahrung' : 'More than 3 years experience'}</p>
                  </div>
                </label>
              </div>
            </RadioGroup>
          </div>
        );
      
      case 2:
        return (
          <div className="space-y-4">
            <p className="text-muted-foreground text-sm mb-4">
              {language === 'de' 
                ? 'Was ist dein Hauptziel beim Trading?'
                : 'What is your main goal with trading?'}
            </p>
            <RadioGroup value={profile.goal} onValueChange={(v) => setProfile({...profile, goal: v})}>
              <div className="space-y-3">
                <label className={`flex items-center gap-4 p-4 rounded-lg border cursor-pointer transition-all ${profile.goal === 'main_income' ? 'border-primary bg-primary/10' : 'border-zinc-800 hover:border-zinc-700'}`}>
                  <RadioGroupItem value="main_income" id="main_income" />
                  <div>
                    <p className="font-medium">{language === 'de' ? 'Haupteinkommen' : 'Main Income'}</p>
                    <p className="text-sm text-muted-foreground">{language === 'de' ? 'Trading als Vollzeit-Beruf' : 'Trading as full-time profession'}</p>
                  </div>
                </label>
                <label className={`flex items-center gap-4 p-4 rounded-lg border cursor-pointer transition-all ${profile.goal === 'side_income' ? 'border-primary bg-primary/10' : 'border-zinc-800 hover:border-zinc-700'}`}>
                  <RadioGroupItem value="side_income" id="side_income" />
                  <div>
                    <p className="font-medium">{language === 'de' ? 'Nebeneinkommen' : 'Side Income'}</p>
                    <p className="text-sm text-muted-foreground">{language === 'de' ? 'Zusätzliches Einkommen neben dem Hauptberuf' : 'Additional income alongside main job'}</p>
                  </div>
                </label>
                <label className={`flex items-center gap-4 p-4 rounded-lg border cursor-pointer transition-all ${profile.goal === 'wealth_building' ? 'border-primary bg-primary/10' : 'border-zinc-800 hover:border-zinc-700'}`}>
                  <RadioGroupItem value="wealth_building" id="wealth_building" />
                  <div>
                    <p className="font-medium">{language === 'de' ? 'Vermögensaufbau' : 'Wealth Building'}</p>
                    <p className="text-sm text-muted-foreground">{language === 'de' ? 'Langfristiger Kapitalaufbau' : 'Long-term capital growth'}</p>
                  </div>
                </label>
              </div>
            </RadioGroup>
          </div>
        );
      
      case 3:
        return (
          <div className="space-y-4">
            <p className="text-muted-foreground text-sm mb-4">
              {language === 'de' 
                ? 'Wie viel Zeit pro Woche widmest du dem Trading?'
                : 'How much time per week do you dedicate to trading?'}
            </p>
            <RadioGroup value={profile.weekly_hours} onValueChange={(v) => setProfile({...profile, weekly_hours: v})}>
              <div className="space-y-3">
                <label className={`flex items-center gap-4 p-4 rounded-lg border cursor-pointer transition-all ${profile.weekly_hours === 'under_5' ? 'border-primary bg-primary/10' : 'border-zinc-800 hover:border-zinc-700'}`}>
                  <RadioGroupItem value="under_5" id="under_5" />
                  <div>
                    <p className="font-medium">{language === 'de' ? 'Weniger als 5 Stunden' : 'Less than 5 hours'}</p>
                    <p className="text-sm text-muted-foreground">{language === 'de' ? 'Gelegentlich' : 'Occasional'}</p>
                  </div>
                </label>
                <label className={`flex items-center gap-4 p-4 rounded-lg border cursor-pointer transition-all ${profile.weekly_hours === '5_to_20' ? 'border-primary bg-primary/10' : 'border-zinc-800 hover:border-zinc-700'}`}>
                  <RadioGroupItem value="5_to_20" id="5_to_20" />
                  <div>
                    <p className="font-medium">{language === 'de' ? '5-20 Stunden' : '5-20 hours'}</p>
                    <p className="text-sm text-muted-foreground">{language === 'de' ? 'Regelmäßig' : 'Regular'}</p>
                  </div>
                </label>
                <label className={`flex items-center gap-4 p-4 rounded-lg border cursor-pointer transition-all ${profile.weekly_hours === 'over_20' ? 'border-primary bg-primary/10' : 'border-zinc-800 hover:border-zinc-700'}`}>
                  <RadioGroupItem value="over_20" id="over_20" />
                  <div>
                    <p className="font-medium">{language === 'de' ? 'Über 20 Stunden' : 'More than 20 hours'}</p>
                    <p className="text-sm text-muted-foreground">{language === 'de' ? 'Vollzeit' : 'Full-time'}</p>
                  </div>
                </label>
              </div>
            </RadioGroup>
          </div>
        );
      
      case 4:
        return (
          <div className="space-y-4">
            <p className="text-muted-foreground text-sm mb-4">
              {language === 'de' 
                ? 'Wie ist deine Risiko-Toleranz?'
                : 'What is your risk tolerance?'}
            </p>
            <RadioGroup value={profile.risk_tolerance} onValueChange={(v) => setProfile({...profile, risk_tolerance: v})}>
              <div className="space-y-3">
                <label className={`flex items-center gap-4 p-4 rounded-lg border cursor-pointer transition-all ${profile.risk_tolerance === 'conservative' ? 'border-primary bg-primary/10' : 'border-zinc-800 hover:border-zinc-700'}`}>
                  <RadioGroupItem value="conservative" id="conservative" />
                  <div>
                    <p className="font-medium">{language === 'de' ? 'Konservativ' : 'Conservative'}</p>
                    <p className="text-sm text-muted-foreground">{language === 'de' ? 'Max. 0.5-1% Risiko pro Trade' : 'Max 0.5-1% risk per trade'}</p>
                  </div>
                </label>
                <label className={`flex items-center gap-4 p-4 rounded-lg border cursor-pointer transition-all ${profile.risk_tolerance === 'moderate' ? 'border-primary bg-primary/10' : 'border-zinc-800 hover:border-zinc-700'}`}>
                  <RadioGroupItem value="moderate" id="moderate" />
                  <div>
                    <p className="font-medium">{language === 'de' ? 'Moderat' : 'Moderate'}</p>
                    <p className="text-sm text-muted-foreground">{language === 'de' ? '1-2% Risiko pro Trade' : '1-2% risk per trade'}</p>
                  </div>
                </label>
                <label className={`flex items-center gap-4 p-4 rounded-lg border cursor-pointer transition-all ${profile.risk_tolerance === 'aggressive' ? 'border-primary bg-primary/10' : 'border-zinc-800 hover:border-zinc-700'}`}>
                  <RadioGroupItem value="aggressive" id="aggressive" />
                  <div>
                    <p className="font-medium">{language === 'de' ? 'Aggressiv' : 'Aggressive'}</p>
                    <p className="text-sm text-muted-foreground">{language === 'de' ? '2-5% Risiko pro Trade' : '2-5% risk per trade'}</p>
                  </div>
                </label>
              </div>
            </RadioGroup>
            
            <div className="mt-6 pt-4 border-t border-zinc-800">
              <Label>{language === 'de' ? 'Geplantes Risiko pro Trade (%)' : 'Planned risk per trade (%)'}</Label>
              <Input
                type="number"
                step="0.1"
                min="0.1"
                max="10"
                value={profile.planned_risk_per_trade}
                onChange={(e) => setProfile({...profile, planned_risk_per_trade: parseFloat(e.target.value) || 1.0})}
                className="mt-2 w-32"
              />
            </div>
          </div>
        );
      
      default:
        return null;
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="glass max-w-lg">
        <DialogHeader>
          <div className="flex items-center gap-3 mb-2">
            <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-gradient-to-br from-emerald-500/20 to-purple-500/20 border border-emerald-500/30">
              <User className="h-5 w-5 text-emerald-400" />
            </div>
            <div>
              <DialogTitle>{language === 'de' ? 'Trader-Profil erstellen' : 'Create Trader Profile'}</DialogTitle>
              <DialogDescription>
                {language === 'de' ? 'Schritt' : 'Step'} {step + 1} {language === 'de' ? 'von' : 'of'} {STEPS.length}
              </DialogDescription>
            </div>
          </div>
          
          {/* Progress bar */}
          <div className="flex gap-1 mt-4">
            {STEPS.map((s, i) => (
              <div 
                key={s.id} 
                className={`flex-1 h-1 rounded-full transition-colors ${i <= step ? 'bg-primary' : 'bg-zinc-800'}`}
              />
            ))}
          </div>
        </DialogHeader>
        
        <div className="py-4 min-h-[300px]">
          {renderStep()}
        </div>
        
        <DialogFooter className="flex gap-2">
          {step > 0 && (
            <Button variant="outline" onClick={handleBack}>
              <ChevronLeft className="h-4 w-4 mr-1" />
              {language === 'de' ? 'Zurück' : 'Back'}
            </Button>
          )}
          {step < STEPS.length - 1 ? (
            <Button onClick={handleNext} disabled={!canProceed()} className="ml-auto">
              {language === 'de' ? 'Weiter' : 'Next'}
              <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          ) : (
            <Button onClick={handleSubmit} disabled={!canProceed() || loading} className="ml-auto glow-primary">
              <Check className="h-4 w-4 mr-1" />
              {language === 'de' ? 'Profil speichern' : 'Save Profile'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default TraderProfileOnboarding;
